import CustomButton from "@components/buttons/CustomButton";
import { CurrencyLogoDropdown } from "@components/dropdowns/CurrencyLogoDropdown";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { primeServices } from "@services/apis/Prime";
import { transactionApiServices } from "@services/apis/Transactions";
import { Network } from "@services/models/Transactions/GetNetworkList";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useMediaQuery } from "react-responsive";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface ConfirmReinitiatePanelProps {
  open: boolean;
  instanceId?: string;
  market: "canada" | "europe";
  onClose: () => void;
  onSuccess?: (instanceId: string, newEventId: number) => void;
}

const ConfirmReinitiatePanel = ({
  open,
  instanceId,
  market,
  onClose,
  onSuccess,
}: ConfirmReinitiatePanelProps) => {
  const { handleError, handleSuccess } = useSnackbarMessage();
  const [loading, setLoading] = useState(false);
  const [walletAddress, setWalletAddress] = useState("");
  const [selectedNetworkId, setSelectedNetworkId] = useState<
    number | undefined
  >(undefined);
  const [networks, setNetworks] = useState<Network[]>([]);
  const [networkLoading, setNetworkLoading] = useState(false);
  const [fieldError, setFieldError] = useState("");
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  useEffect(() => {
    if (!open) {
      setWalletAddress("");
      setSelectedNetworkId(undefined);
      setFieldError("");
      setLoading(false);
      return;
    }

    const fetchNetworks = async () => {
      try {
        setNetworkLoading(true);
        const response = await transactionApiServices.getNetworksList();
        setNetworks(response.data || []);
      } catch (error: any) {
        handleError({
          message:
            error?.response?.data?.msg ||
            error?.message ||
            "Failed to load networks",
        });
      } finally {
        setNetworkLoading(false);
      }
    };

    void fetchNetworks();
  }, [open]);

  const selectedNetwork = networks.find(
    (network) => network.networkId === selectedNetworkId,
  );
  const walletNetworkSymbol = selectedNetwork?.networkSymbol || "";

  const handleReinitiate = async () => {
    if (!instanceId) return;

    const address = walletAddress.trim();
    const hasAddress = !!address;
    const hasNetwork = !!walletNetworkSymbol.trim();
    if (hasAddress !== hasNetwork) {
      setFieldError(
        "Both wallet address and network are required when updating wallet details.",
      );
      return;
    }

    if (hasAddress && hasNetwork && selectedNetwork?.networkRegex) {
      try {
        const regex = new RegExp(selectedNetwork.networkRegex);
        if (!regex.test(address)) {
          setFieldError(
            `Invalid wallet address for ${selectedNetwork.networkName || selectedNetwork.networkSymbol}.`,
          );
          return;
        }
      } catch {
        setFieldError("Invalid network address format configuration.");
        return;
      }
    }

    setFieldError("");
    setLoading(true);
    try {
      const payload =
        hasAddress && hasNetwork
          ? {
              walletAddress: address,
              walletNetworkSymbol: walletNetworkSymbol.trim(),
            }
          : {};
      const response =
        market === "canada"
          ? await primeServices.cadHoldReinitiate(instanceId, payload)
          : await primeServices.ivyHoldReinitiate(instanceId, payload);
      handleSuccess({
        message: response?.msg || "Instance re-initiated successfully",
      });
      onSuccess?.(instanceId, 6);
      onClose();
    } catch (error: any) {
      handleError({
        message:
          error?.response?.data?.msg ||
          error?.message ||
          "Failed to re-initiate instance",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlidingPanel
      isOpen={open && !!instanceId}
      onClose={loading ? () => {} : onClose}
      onClickCross={loading ? () => {} : onClose}
      heading="Confirm Re-initiate"
      showConfirm={false}
      showCross={!loading}
      isMobile={isMobile}
    >
      <div className="p-4 flex flex-col gap-4">
        <p className="text-xs text-text-subtle">
          You are about to re-initiate settlement for instance{" "}
          <strong className="text-text-base">{instanceId}</strong>. Leave wallet
          fields empty to retry with existing details, or update both fields
          together.
        </p>

        <GlobalTextInputField
          title="Wallet Address (optional)"
          value={walletAddress}
          onChange={(e) => {
            setWalletAddress(e.target.value);
            if (fieldError) setFieldError("");
          }}
          placeholder="Enter wallet address"
          fontSize="12px"
          backgroundColor="bg-bg-page-body"
          inputBorder="border-border-subtle rounded-md"
        />

        {networkLoading ? (
          <Skeleton height={44} />
        ) : (
          <CurrencyLogoDropdown
            listOfValues={networks.map((network) => ({
              id: network.networkId,
              currency: network.networkSymbol,
              currencyLogo: network.networkImg,
              networkName: network.networkName,
            }))}
            value={selectedNetworkId}
            onChange={(value) => {
              setSelectedNetworkId(Number(value));
              if (fieldError) setFieldError("");
            }}
            label="Select Wallet network (optional)"
            color="bg-main-container-header"
            baseColor="bg-bg-page-body"
            borderColor="border-subtle"
            expandedBorderColor="border-button-primary"
          />
        )}

        {fieldError && (
          <p className="text-[11px] text-text-status-red">{fieldError}</p>
        )}

        <div className="flex flex-col gap-2">
          <CustomButton
            text="RE-INITIATE"
            isActive={true}
            isButtonLoading={loading}
            onClick={handleReinitiate}
          />
          <CustomButton
            text="CANCEL"
            isActive={!loading}
            isHollow
            onClick={onClose}
          />
        </div>
      </div>
    </SlidingPanel>
  );
};

export default ConfirmReinitiatePanel;
